import { useState, useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { useTranslation } from "react-i18next";
import { fetchDocumentById } from "../../store/actions/documentsActions";
import {
    createPaymentAction,
    fetchPriceApparat,
} from "../../store/actions/paymentsActions";
import pdfLogo from "../../assets/img/pdf.png";
import docLogo from "../../assets/img/doc.png";
import pptxLogo from "../../assets/img/pptx.png";
import xlsxLogo from "../../assets/img/xlsx.png";
import Header from "./Header";
import DocumentPreview from "./DocumentPreview";
import PrintSettings from "./PrintSettings";
import DocumentViewer from "./DocumentViewer";
import PaymentModal from "./PaymentModal";
import LoadingOverlay from "./LoadingOverlay";
import WarningMessage from "./WarningMessage";
import ApparatModeWarning from "./ApparatModeWarning";
import NumberKeyboardWrapper from "./NumberKeyboardWrapper";

function EditDoc() {
    const [copyCount, setCopyCount] = useState(1);
    const [printType, setPrintType] = useState("print_all");
    const [customPages, setCustomPages] = useState("");
    const [orientation, setOrientation] = useState("portrait");
    const [printSide, setPrintSide] = useState("one_sided");
    const [printColor, setPrintColor] = useState("black_white");
    const [totalPages, setTotalPages] = useState(1);
    const [showKeyboard, setShowKeyboard] = useState(false);
    const [showModal, setShowModal] = useState(false);
    const [price, setPrice] = useState(0);
    const [isDocumentRendered, setIsDocumentRendered] = useState(false);
    const [availablePapers, setAvailablePapers] = useState(0);
    const { id } = useParams();
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const [loadingMessage, setLoadingMessage] = useState(0);
    const { currentDocument, loading, error } = useSelector(
        (state) => state.documentsList,
    );
    const { loadingPayment, priceApparat } = useSelector(
        (state) => state.payments,
    );
    const { t } = useTranslation();

    const messages = [
        t("editDoc.loading_messages.processing"),
        t("editDoc.loading_messages.almost_done"),
        t("editDoc.loading_messages.waiting"),
        t("editDoc.loading_messages.finishing"),
    ];

    useEffect(() => {
        if (loadingPayment) {
            const interval = setInterval(() => {
                setLoadingMessage((prev) => (prev + 1) % messages.length);
            }, 3000);
            return () => clearInterval(interval);
        }
    }, [loadingPayment, messages.length]);

    useEffect(() => {
        dispatch(fetchDocumentById(id));
        dispatch(fetchPriceApparat());
    }, [dispatch, id]);

    // Режимы аппарата могли выключить (веб-панель/бот) уже после того, как
    // пользователь выбрал цвет/дуплекс — если выбранная опция стала
    // недоступна, откатываем на безопасный дефолт, а не оставляем скрытую
    // невалидную комбинацию, которую сервер всё равно отклонит на оплате.
    useEffect(() => {
        if (priceApparat?.is_color === false && printColor === "color") {
            setPrintColor("black_white");
        }
        if (priceApparat?.allow_duplex === false && printSide === "two_sided") {
            setPrintSide("one_sided");
        }
    }, [priceApparat, printColor, printSide]);

    useEffect(() => {
        import("axios").then(({ default: axios }) => {
            const deviceToken = localStorage.getItem("deviceToken");
            axios
                .get(
                    `${process.env.REACT_APP_BASEURL}/api/v1/admin/apparats/pages`,
                    {
                        headers: { "Device-Token": `${deviceToken}` },
                    },
                )
                .then((response) =>
                    // max_tray_pages — "с чем одним документом точно справится
                    // аппарат" (максимум среди лотков). Аппарат ещё без лотков
                    // (легаси) отдаёт его же равным count_pages, см. backend
                    // app/api/v1/admin/admin.py::get_apparat_pages.
                    setAvailablePapers(
                        response.data.max_tray_pages ??
                            response.data.count_pages ??
                            0,
                    ),
                )
                .catch((error) =>
                    console.error("Error fetching available papers:", error),
                );
        });
    }, []);

    const normalizePages = (pages) => {
        if (!pages) return "";

        return pages
            .split(",")
            .map((p) => p.trim())
            .filter((p) => p !== "" && !isNaN(p))
            .join(",");
    };

    const handlePayment = async () => {
        setShowModal(false);
        let boolOrientation = orientation === "portrait" ? false : true;
        let isOneSide = printSide === "one_sided" ? true : false;
        let pagesToSend = customPages;
        if (printType === "first_page") {
            pagesToSend = "1";
        } else if (printType === "last_page") {
            pagesToSend = totalPages.toString();
        }

        const data = [
            {
                document_id: parseInt(id),
                amount: price,
                custom_pages: pagesToSend,
                is_album_orientation: boolOrientation,
                is_one_side: isOneSide,
                is_all_pages: printType === "print_all",
                copy_count: copyCount,
                status: "pending",
                // Прямой смысл: true = ЦВЕТ. Раньше здесь слался
                // перевёрнутый флаг (true = Ч/Б), а сервер разворачивал его
                // обратно (KIOSK_SENDS_INVERTED_IS_COLOR). Конвенцию убрали
                // разом на всех клиентах и на бэкенде — не возвращать.
                is_color: printColor === "color",
                count_pages:
                    printSide === "one_sided"
                        ? (printType === "print_all"
                              ? totalPages
                              : printType === "custom"
                                ? customPages
                                      .split(",")
                                      .flatMap((range) => {
                                          const trimmedRange = range.trim();
                                          const [start, end] =
                                              trimmedRange.includes("-")
                                                  ? trimmedRange
                                                        .split("-")
                                                        .map(Number)
                                                  : [Number(trimmedRange)];
                                          return end
                                              ? Array.from(
                                                    { length: end - start + 1 },
                                                    (_, i) => start + i,
                                                )
                                              : [Number(trimmedRange)];
                                      })
                                      .filter(
                                          (page) =>
                                              page >= 1 && page <= totalPages,
                                      ).length
                                : 1) * copyCount // Умножаем на copyCount
                        : requiredSheets,
            },
        ];

        try {
            const resultAction = await dispatch(createPaymentAction(data));
            if (createPaymentAction.fulfilled.match(resultAction)) {
                navigate(`/payments`, {
                    state: { from: `/printing/show_list/${id}` },
                });
            } else {
                console.error(
                    "Ошибка создания платежа:",
                    resultAction.payload || t("editDoc.payment_error"),
                );
            }
        } catch (error) {
            console.error(
                "Ошибка при отправке запроса на сервер:",
                error.message,
            );
        }
    };

    const pagesToPrint = useMemo(() => {
        let pages = totalPages;
        if (printType === "first_page" || printType === "last_page") {
            pages = 1;
        } else if (printType === "custom" && customPages) {
            const parsedPages = customPages
                .split(",")
                .flatMap((range) => {
                    const trimmedRange = range.trim();
                    const [start, end] = trimmedRange.includes("-")
                        ? trimmedRange.split("-").map(Number)
                        : [Number(trimmedRange)];
                    return end
                        ? Array.from(
                              { length: end - start + 1 },
                              (_, i) => start + i,
                          )
                        : [Number(trimmedRange)];
                })
                .filter((page) => page >= 1 && page <= totalPages);
            pages = parsedPages.length;
        }
        return pages;
    }, [totalPages, printType, customPages]);

    const requiredSheets = useMemo(() => {
        let sheetsPerCopy;
        if (printSide === "one_sided") {
            sheetsPerCopy = pagesToPrint;
        } else {
            sheetsPerCopy = Math.ceil(pagesToPrint / 2); // Делим на 2 для двусторонней печати
        }
        return sheetsPerCopy * copyCount;
    }, [pagesToPrint, printSide, copyCount]);

    const calculateTotalCost = useMemo(() => {
        const {
            price_for_black_and_white = 30,
            price_for_color = 60,
            price_for_black_and_white_duplex = 50,
            price_for_color_duplex = 100,
            price_for_photo_bw = 50,
            price_for_photo_color = 80,
        } = priceApparat || {};

        // Фото (currentDocument.tariff === "photo") печатается дороже — краска
        // уходит как сплошная заливка. Дуплекс на фото не влияет, см.
        // app/api/common/tariffs.py на бэкенде: тариф всегда одностраничный.
        const costPerSheet =
            currentDocument?.tariff === "photo"
                ? printColor === "black_white"
                    ? price_for_photo_bw
                    : price_for_photo_color
                : printSide === "one_sided"
                  ? printColor === "black_white"
                      ? price_for_black_and_white
                      : price_for_color
                  : printColor === "black_white"
                    ? price_for_black_and_white_duplex
                    : price_for_color_duplex;

        const totalCost = requiredSheets * costPerSheet;
        return totalCost;
    }, [requiredSheets, printSide, printColor, priceApparat, currentDocument]);

    useEffect(() => {
        setPrice(calculateTotalCost);
    }, [calculateTotalCost]);

    useEffect(() => {
        setTotalPages(1);
        setIsDocumentRendered(false);
    }, [id]);

    const ordersPaused = priceApparat?.accept_orders === false;
    const photoBlocked =
        currentDocument?.tariff === "photo" &&
        priceApparat?.allow_photo_print === false;
    const canPrint =
        isDocumentRendered &&
        availablePapers >= requiredSheets &&
        !ordersPaused &&
        !photoBlocked;
    const showWarning = availablePapers > 0 && availablePapers < requiredSheets;

    if (error) {
        return (
            <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.3 }}
                className="text-center text-red-500 text-[2.5vh] mt-10"
            >
                {t("editDoc.error")}:{" "}
                {typeof error === "object"
                    ? error.detail || t("editDoc.error_default")
                    : error}
            </motion.div>
        );
    }

    if (!currentDocument) {
        return (
            <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.3 }}
                className="text-center text-white text-[2.5vh] mt-10"
            >
                {t("editDoc.error")}
            </motion.div>
        );
    }

    // Добавляем проверку на loading или несоответствие ID
    if (loading || (currentDocument && currentDocument.id !== Number(id))) {
        return (
            <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.3 }}
                className="text-center text-white text-[2.5vh] mt-10"
            >
                {t("editDoc.no_document")}
            </motion.div>
        );
    }

    return (
        <>
            <LoadingOverlay
                loading={loading}
                loadingPayment={loadingPayment}
                messages={messages}
                loadingMessage={loadingMessage}
                t={t}
            />
            <AnimatePresence>
                {showModal && (
                    <PaymentModal
                        showModal={showModal}
                        setShowModal={setShowModal}
                        handlePayment={handlePayment}
                        printType={printType}
                        customPages={customPages}
                        printColor={printColor}
                        orientation={orientation}
                        printSide={printSide}
                        copyCount={copyCount}
                        calculateTotalCost={calculateTotalCost}
                        t={t}
                    />
                )}
            </AnimatePresence>
            <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.3 }}
                className="flex justify-center mt-[14.2vh] relative"
            >
                <div className="w-[97vw] h-[66vh] bg-[#7F7F7F66] rounded-[48px] flex flex-col relative p-4">
                    <Header t={t} />
                    <DocumentPreview
                        currentDocument={currentDocument}
                        copyCount={copyCount}
                        setCopyCount={setCopyCount}
                        pdfLogo={pdfLogo}
                        docLogo={docLogo}
                        pptxLogo={pptxLogo}
                        xlsxLogo={xlsxLogo}
                        t={t}
                    />
                    <div className="flex flex-row-reverse gap-3 justify-between mx-10">
                        <PrintSettings
                            printType={printType}
                            setPrintType={setPrintType}
                            customPages={customPages}
                            setCustomPages={setCustomPages}
                            orientation={orientation}
                            setOrientation={setOrientation}
                            printSide={printSide}
                            setPrintSide={setPrintSide}
                            printColor={printColor}
                            setPrintColor={setPrintColor}
                            setShowKeyboard={setShowKeyboard}
                            canPrint={canPrint}
                            setShowModal={setShowModal}
                            totalPages={totalPages}
                            allowColor={priceApparat?.is_color !== false}
                            allowDuplex={priceApparat?.allow_duplex !== false}
                            t={t}
                        />
                        <motion.div className="ml-[1vw] mt-[2vh] rounded-lg relative flex flex-row">
                            <DocumentViewer
                                key={currentDocument.id}
                                currentDocument={currentDocument}
                                printType={printType}
                                customPages={customPages}
                                printColor={printColor}
                                onLoadSuccess={(pages) => {
                                    setTotalPages(pages);
                                    setIsDocumentRendered(true);
                                }}
                                t={t}
                            />
                        </motion.div>
                    </div>
                    <WarningMessage
                        showWarning={showWarning}
                        availablePapers={availablePapers}
                        requiredSheets={requiredSheets}
                        t={t}
                    />
                    <ApparatModeWarning
                        ordersPaused={ordersPaused}
                        photoBlocked={photoBlocked}
                        t={t}
                    />
                    <NumberKeyboardWrapper
                        showKeyboard={showKeyboard}
                        customPages={customPages}
                        setCustomPages={setCustomPages}
                        setShowKeyboard={setShowKeyboard}
                        maxPage={totalPages} // ← добавить
                        t={t}
                    />
                </div>
            </motion.div>
        </>
    );
}

export default EditDoc;
